import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangleIcon, CalendarClockIcon, Loader2Icon, PlayIcon, RotateCcwIcon, VideoOffIcon } from 'lucide-react';
import { PosterFallback } from './PosterFallback';
import { hasPlayableSource } from '../utils/production';
import { buttonClass } from '../utils/styles';
type PlayerState = 'idle' | 'loading' | 'playing' | 'paused' | 'ended' | 'error';
interface VideoPlayerProps {
  src: string | null;
  poster: string | null;
  title: string;
  autoPlay?: boolean;
  /** Increment to request playback from an external button. */
  playRequest?: number;
  /** When set, the production is not yet released and cannot be played. */
  availableFromLabel?: string | null;
}
const STATUS_MESSAGES: Record<PlayerState, string> = {
  idle: '',
  loading: 'Cargando video',
  playing: 'Reproduciendo',
  paused: 'En pausa',
  ended: 'Reproducción finalizada',
  error: 'No se pudo cargar el video'
};
const frameClass = 'relative aspect-video w-full overflow-hidden rounded-lg bg-black ring-1 ring-graphite';
export function VideoPlayer({
  src,
  poster,
  title,
  autoPlay = false,
  playRequest = 0,
  availableFromLabel = null
}: VideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<PlayerState>('idle');
  const [started, setStarted] = useState(false);
  const canPlay = hasPlayableSource(src) && !availableFromLabel;
  useEffect(() => {
    setState('idle');
    setStarted(false);
  }, [src]);
  const play = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    setStarted(true);
    setState('loading');
    try {
      if (video.ended) video.currentTime = 0;
      await video.play();
    } catch (err) {
      const name = err instanceof DOMException ? err.name : '';
      if (name === 'NotAllowedError') {
        setState('idle');
        setStarted(false);
      } else if (name !== 'AbortError') {
        setState('error');
      }
    }
  }, []);
  const retry = useCallback(() => {
    videoRef.current?.load();
    void play();
  }, [play]);
  useEffect(() => {
    if (autoPlay && canPlay) void play();
  }, [autoPlay, canPlay, play]);
  useEffect(() => {
    if (playRequest === 0) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    containerRef.current?.scrollIntoView({
      block: 'center',
      behavior: reduce ? 'auto' : 'smooth'
    });
    if (canPlay) void play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playRequest]);
  const cover = poster ? <img src={poster} alt="" className="absolute inset-0 h-full w-full object-cover" data-id="element-263" data-mp="16015pfp8n066" /> : <div className="absolute inset-0" data-id="element-264" data-mp="10xsbkg0mps2h">
      <PosterFallback data-id="element-265" data-mp="2hdmsiu7mkpoj" />
    </div>;
  if (!canPlay) {
    const upcoming = !!availableFromLabel;
    const Icon = upcoming ? CalendarClockIcon : VideoOffIcon;
    return <div ref={containerRef} className={frameClass} data-id="element-266" data-mp="2cgmoeouc5x02">
        {cover}
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-night/80 px-6 text-center" data-id="element-267" data-mp="3ubdnfgc4ugfr">
          <Icon aria-hidden="true" className="h-8 w-8 text-white" data-id="element-268" data-mp="375f4bzntmwu" />
          <p className="mt-4 font-display text-base font-bold text-white md:text-lg" data-id="element-269" data-mp="3lc8klxtjg0xs">
            {upcoming ? 'Próximo estreno' : 'Video aún no disponible'}
          </p>
          <p className="mt-2 max-w-sm text-sm text-mist" data-id="element-270" data-mp="hv087thkxtag">
            {upcoming ? `Disponible a partir del ${availableFromLabel}.` : 'Esta producción todavía no tiene una fuente de video configurada.'}
          </p>
        </div>
      </div>;
  }
  return <div ref={containerRef} className={frameClass} data-id="element-271" data-mp="d41pyod16l4k">
      <video ref={videoRef} src={src ?? undefined} poster={poster ?? undefined} preload="metadata" playsInline controls={started && state !== 'error'} aria-label={`Reproductor de video: ${title}`} className="h-full w-full bg-black object-contain" onWaiting={() => setState('loading')} onPlaying={() => setState('playing')} onPause={() => setState(s => s === 'error' || s === 'ended' ? s : 'paused')} onSeeked={() => setState(s => s === 'loading' && videoRef.current?.paused ? 'paused' : s)} onEnded={() => setState('ended')} onError={() => setState('error')} data-id="element-272" data-mp="d2qlsnfobbcx" />

      {!started && state !== 'error' && <button type="button" onClick={() => void play()} aria-label={`Reproducir ${title}`} className="group absolute inset-0 flex items-center justify-center focus-visible:outline-none" data-id="element-273" data-mp="3vwi06ozr1ul9">
          {poster && <img src={poster} alt="" className="absolute inset-0 h-full w-full object-cover" data-id="element-274" data-mp="3i3s6rzhw5ne4" />}
          <span aria-hidden="true" className="absolute inset-0 bg-black/35" data-id="element-275" data-mp="jd8jx4y1y2na" />
          <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-action text-white shadow-xl transition-transform duration-150 ease-cine group-hover:scale-105 group-active:scale-95 group-focus-visible:ring-4 group-focus-visible:ring-white/70 md:h-20 md:w-20" data-id="element-276" data-mp="emjv5xdrbjru">
            <PlayIcon className="ml-1 h-7 w-7 fill-current md:h-8 md:w-8" data-id="element-277" data-mp="vvm57wkvad6d" />
          </span>
        </button>}

      {state === 'loading' && <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40" data-id="element-278" data-mp="zxz7gb0ensye">
          <Loader2Icon aria-hidden="true" className="h-10 w-10 animate-spin text-white" data-id="element-279" data-mp="14vr93wquqtfs" />
        </div>}

      {state === 'error' && <div className="absolute inset-0 flex flex-col items-center justify-center bg-night/90 px-6 text-center" data-id="element-280" data-mp="1emc6xhypbtxn">
          <AlertTriangleIcon aria-hidden="true" className="h-8 w-8 text-white" data-id="element-281" data-mp="1jlortx60iqxl" />
          <p className="mt-4 font-display text-base font-bold text-white md:text-lg" data-id="element-282" data-mp="1nms4ybwoe8v4">No se pudo cargar el video</p>
          <p className="mt-2 max-w-sm text-sm text-mist" data-id="element-283" data-mp="1slbx4u1v4gvx">
            La fuente no responde o el formato no es compatible. Usa un enlace directo MP4 o WebM.
          </p>
          <button type="button" onClick={retry} className={buttonClass('secondary', 'md', 'mt-6')} data-id="element-284" data-mp="3omnawafxzcog">
            <RotateCcwIcon className="h-4 w-4" data-id="element-285" data-mp="3klles2hbngqp" />
            Reintentar
          </button>
        </div>}

      {state === 'ended' && <div className="absolute inset-0 flex items-center justify-center bg-black/60" data-id="element-286" data-mp="3tn1mg2gr54r8">
          <button type="button" onClick={() => void play()} className={buttonClass('secondary', 'md')} data-id="element-287" data-mp="c6tjum1mua5m">
            <RotateCcwIcon className="h-4 w-4" data-id="element-288" data-mp="79baiujpdsx3" />
            Volver a ver
          </button>
        </div>}

      <p className="sr-only" aria-live="polite" data-id="element-289" data-mp="l6ztfwu7mpzi">
        {STATUS_MESSAGES[state]}
      </p>
    </div>;
}